import { FC, ReactNode, useId } from 'react'
import clsx from 'clsx'
import { Offcanvas } from 'react-bootstrap'
import { useT } from '_metronic/i18n/Metronici18n'
import styles from './styles/Drawer.module.css'

type DrawerSize = 'sm' | 'md' | 'lg'

type DrawerProps = {
  show: boolean
  onHide: () => void
  title: ReactNode
  subtitle?: ReactNode
  children?: ReactNode
  /** Ação principal (ex.: botão de confirmar), fixa no rodapé enquanto o corpo rola. */
  footer?: ReactNode
  placement?: 'start' | 'end'
  /** Largura: sm 400px, md 640px, lg 900px; nunca passa da tela. */
  size?: DrawerSize
  /** Sem X, Esc nem clique fora: para quando fechar perderia uma operação em andamento. */
  disableClose?: boolean
  /** `false` quando quem fecha decide para onde o foco vai (ex.: um campo da tela). */
  restoreFocus?: boolean
  /** Classe do painel (ex.: tema da tela, já que ele renderiza fora dela). */
  className?: string
  /** Classe do corpo; o padrão tem o espaçamento do tema. */
  bodyClassName?: string
}

/**
 * Painel lateral declarativo: a tela controla `show` e passa o conteúdo com o estado atual, sem
 * passar pelo `useModal`. Título ligado ao painel por `aria-labelledby`, fechar com nome
 * acessível, foco preso no painel enquanto aberto e devolvido a quem abriu ao fechar.
 *
 * @example
 * <Drawer show={aberto} onHide={fechar} title='Revisar' footer={<Button>Assinar</Button>}>
 *   {conteudo}
 * </Drawer>
 */
export const Drawer: FC<DrawerProps> = ({
  show,
  onHide,
  title,
  subtitle,
  children,
  footer,
  placement = 'end',
  size = 'md',
  disableClose = false,
  restoreFocus = true,
  className,
  bodyClassName,
}) => {
  const { t } = useT()
  const idTitulo = useId()

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement={placement}
      backdrop={disableClose ? 'static' : true}
      keyboard={!disableClose}
      restoreFocus={restoreFocus}
      className={clsx(styles.drawer, styles[size], className)}
      aria-labelledby={idTitulo}
    >
      <Offcanvas.Header
        closeButton={!disableClose}
        closeLabel={t('common.close')}
        className='border-bottom align-items-start'
      >
        <div className='d-flex flex-column'>
          <Offcanvas.Title as='h2' id={idTitulo} className='fs-4 fw-bold'>
            {title}
          </Offcanvas.Title>
          {subtitle && <span className='fs-7 text-gray-700'>{subtitle}</span>}
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className={bodyClassName}>{children}</Offcanvas.Body>

      {footer && <div className={styles.footer}>{footer}</div>}
    </Offcanvas>
  )
}
